import React, {
  ComponentProps,
  RefObject,
  useCallback,
  useRef,
  useState,
} from 'react';
import { Modal, TouchableOpacity } from 'react-native';

import { DateRange } from '@components/DateRangePicker/DateRangePickerModal';
import { ContactType } from '@components/Invite/Contact';
import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import {
  PopupTaskFilterProjectV3,
  PopupTaskFilterProjectDateRangeV3,
} from '@components/Web/PopupTaskFilterProjectV3.web';
import { Project, TaskPriority } from '@graphql/generated';
import { useOutsideClick } from '@hooks/useOutsideClick.web';
import documentIcons from '@themes/documentIcons';
import Images from '@themes/images';
import { getScaledSize, SCALED_DIMENSIONS } from '@themes/theme';

type PositionTuple = ['left' | 'right', 'top' | 'bottom'];

type Position = PositionTuple | 'top' | 'bottom' | 'left' | 'right';

type Props = Omit<ComponentProps<typeof TouchableOpacity>, 'onPress'> & {
  title: string;
  iconName: keyof typeof Images | keyof typeof documentIcons;
  onDismiss?: any;
  badgeNumber?: string;

  onSelectPriority?: (item: TaskPriority[]) => void;
  selectedPriorities?: TaskPriority[];

  onSelectAssignee?: (ids: ContactType[]) => void;
  selectedAssignee?: ContactType[];
  initialAssignees?: ContactType[];

  selectedDueDateStrList?: string[];
  onSelectedDueDateStrList?: (x: string[]) => void;

  onSelectDuedate?: (dr: DateRange) => void;
  selectedDuedate?: DateRange;

  selectedProjects?: Project[];
  onSelectProjects?: (projects: Project[]) => void;

  hideSearch?: boolean;

  boxProps: Partial<{
    position: Position;
    offset: [number, number] | number;
  }>;
};

export const PopupTaskButtonWebV3 = ({
  onDismiss,
  boxProps: { position, offset, ...popupPropsRest },
  title: xtitle,
  iconName,

  selectedProjects = [],
  onSelectProjects,
  selectedPriorities = [],
  onSelectPriority,

  initialAssignees,
  selectedAssignee = [],
  onSelectAssignee,

  selectedDueDateStrList = [],
  onSelectedDueDateStrList,

  // selectedStatus = '',
  // onSelectStatus,

  selectedDuedate,
  onSelectDuedate,
  hideSearch = false,
}: Props) => {
  const [visible, setVisible] = useState(false);

  const togglePopup = () => setVisible((v) => !v);

  const isEventPointInArea = (
    ref: RefObject<HTMLDivElement>,
    event: MouseEvent
  ) => {
    const rect = ref?.current?.getBoundingClientRect();
    return (
      rect &&
      event.x > rect.x &&
      event.x < rect.x + rect.width &&
      event.y > rect.y &&
      event.y < rect.y + rect.height
    );
  };

  const ref2 = useRef<HTMLDivElement>();

  const iconCloseRef = useRef<HTMLDivElement>();

  const isEventPointInModal = (event: MouseEvent) => {
    return isEventPointInArea(ref2, event);
  };

  const isEventPointInCloseIcon = (event: MouseEvent) => {
    return isEventPointInArea(iconCloseRef, event);
  };
  const clearState = () => {
    onSelectAssignee?.([]);
    onSelectedDueDateStrList?.([]);
    onSelectProjects?.([]);
    onSelectPriority?.([]);
    onSelectDuedate?.({});
  };
  const handleOutsideClick = useCallback((event: MouseEvent) => {
    if (!isEventPointInModal(event)) {
      if (isEventPointInCloseIcon(event)) {
        clearState();
      } else {
        setVisible(false);
      }
    }
  }, []);

  const ref = useOutsideClick<HTMLDivElement>(handleOutsideClick);

  const [initialSection, setInitialSection] = useState<
    'projects' | 'assignee' | 'duedate' | 'priority'
  >('projects');

  // Store the natural dimensions of the popup container
  const [layout, setLayout] = useState<
    { width: number; height: number } | undefined
  >();
  const [currentPanel, setCurrentPanel] = useState<'main' | 'dates'>('main');
  const handleSelectDatesClick = () => {
    // When entering date range panel, we plan to open "duedate" section when we come back
    setInitialSection('duedate');
    setCurrentPanel('dates');
  };
  const handleDateRangeSelected = (dr: DateRange) => {
    onSelectedDueDateStrList?.([]);
    onSelectDuedate?.(dr);
    // go back to the main panel
    setCurrentPanel('main');
  };

  // ================= Close popup & reset panel =================

  const badgeNumber =
    selectedProjects?.length +
    selectedAssignee?.length +
    selectedPriorities?.length +
    selectedDueDateStrList?.length +
    (selectedDuedate?.start || selectedDuedate?.end ? 1 : 0);
  const [mouseOver, setMouseOver] = useState(false);

  const adjustBounds = (
    position: Props['boxProps']['position'] = 'bottom',
    offset: Props['boxProps']['offset'] = 0,
    client: DOMRect,
    popupLayout: { width: number; height: number }
  ) => {
    const [offsetLeftRight, offsetTopBottom] =
      typeof offset === 'number' ? [offset, offset] : offset;

    const normalizedPosition = (typeof position !== 'string' && position) ||
      ((position === 'left' || position === 'right') && [position, 'bottom']) ||
      ((position === 'top' || position === 'bottom') && ['left', position]) || [
        'left',
        'bottom',
      ];

    const [leftRight, topBottom] = normalizedPosition;
    const bounds = {
      top:
        topBottom === 'top'
          ? client.top - popupLayout.height - offsetTopBottom
          : client.top + client.height + offsetTopBottom,
      left:
        leftRight === 'left'
          ? client.left + client.width - popupLayout.width - offsetLeftRight
          : client.left + offsetLeftRight,
    };

    return bounds;
  };

  const adjustedBounds =
    ref.current &&
    layout &&
    adjustBounds(position, offset, ref.current.getBoundingClientRect(), layout);

  const onModalDismiss = () => {
    setInitialSection('projects');
    setCurrentPanel('main');
    onDismiss?.();
    setVisible(false);
  };

  const backgroundColor1 = () => {
    if (mouseOver || badgeNumber !== 0 || visible) return 'grey01';
    return 'white';
  };

  return (
    <Box ref={ref}>
      <TouchableOpacity
        accessibilityLabel={xtitle}
        onPress={() => {
          togglePopup();
        }}>
        <Box
          height={getScaledSize(SCALED_DIMENSIONS.BUTTON_HEIGHT)}
          flexDirection='row'
          py='xs'
          px='m'
          onMouseEnter={() => {
            setMouseOver(true);
          }}
          onMouseLeave={() => {
            setMouseOver(false);
          }}
          backgroundColor={backgroundColor1()}
          borderRadius='xs'
          alignItems='center'
          justifyContent='center'>
          <Icon variant='s' name={iconName} color='black' />
          <Text marginLeft='xs' variant='labelSmall' color='textPrimary'>
            {xtitle}
          </Text>
          {badgeNumber > 0 ? (
            <Box
              marginLeft='xs'
              minWidth={20}
              height={20}
              alignItems='center'
              justifyContent='center'
              backgroundColor='greenSecondary'
              borderRadius='s'>
              <Text variant='webBodyHintSubline' color='white'>
                {badgeNumber}
              </Text>
            </Box>
          ) : (
            <Box />
          )}
          {badgeNumber > 0 ? (
            <Box ref={iconCloseRef}>
              <Icon
                marginLeft='xs'
                variant='xxxs'
                name='Close'
                onPress={() => {
                  clearState();
                }}
                color='black'
              />
            </Box>
          ) : (
            <Box />
          )}
        </Box>
      </TouchableOpacity>

      {!layout && (
        <Box
          left='-200vw'
          position='absolute'
          visible={!layout}
          onLayout={({ nativeEvent: { layout } }) => {
            setLayout((curr) => curr ?? layout);
          }}>
          <PopupTaskFilterProjectV3
            selectedProjects={selectedProjects}
            hideSearch={hideSearch}
            {...popupPropsRest}
          />
        </Box>
      )}
      <Modal
        visible={visible}
        transparent
        onDismiss={() => onModalDismiss()}
        onRequestClose={() => onModalDismiss()}>
        <Box ref={ref2} style={{ ...adjustedBounds }} width={layout?.width}>
          <Box flexShrink={1}>
            {currentPanel === 'main' ? (
              <PopupTaskFilterProjectV3
                selectedProjects={selectedProjects}
                {...popupPropsRest}
                onSelectProject={(ids) => {
                  onSelectProjects && onSelectProjects(ids);
                }}
                initialAssignees={initialAssignees}
                selectedAssignees={selectedAssignee}
                onSelectAssignee={(ids) => {
                  onSelectAssignee && onSelectAssignee(ids);
                }}
                onSelectPriority={(item) => {
                  onSelectPriority && onSelectPriority(item);
                }}
                selectedPriorities={selectedPriorities}
                selectedDueDateStrList={selectedDueDateStrList}
                onSelectedDueDateStrList={onSelectedDueDateStrList}
                selectedDueDateRange={selectedDuedate}
                onSelectDueDateRange={onSelectDuedate}
                onPressSelectDates={handleSelectDatesClick}
                initiallyOpenSection={initialSection}
                hideSearch={hideSearch}
              />
            ) : (
              <PopupTaskFilterProjectDateRangeV3
                selectedDueDateRange={selectedDuedate}
                onSelectDueDateRange={(dr) => handleDateRangeSelected(dr)}
                isRangeSelector={false}
              />
            )}
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};
